import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { Building2, GitBranch, MessageSquareWarning } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { fetchCalls } from '../api';

const COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#ef4444', '#0ea5e9', '#7c3aed', '#64748b'];

const CONSULTED_OR_BETTER_STAGES = new Set([
  'consultation_provided',
  'appointment_option_offered',
  'callback_or_message_planned',
  'appointment_booked'
]);

const NEXT_STEP_OR_BOOKING_STAGES = new Set([
  'appointment_option_offered',
  'callback_or_message_planned',
  'appointment_booked'
]);

const FUNNEL_STAGES = [
  {
    key: 'appointment_or_consultation',
    title: 'Целевые обращения',
    description: 'Звонки, где клиент обсуждает запись, услугу, цену, врача или возможность визита',
    filter: { call_type: 'appointment_or_consultation' },
    getCount: (appointmentCalls) => appointmentCalls.length
  },
  {
    key: 'consulted_or_better',
    title: 'Потребность разобрана',
    description: 'Администратор дал предметную консультацию или довел разговор до следующего шага',
    filter: { funnel_stage: 'dental_consulted_or_better' },
    getCount: (appointmentCalls) => appointmentCalls.filter((item) => CONSULTED_OR_BETTER_STAGES.has(item.funnelStage)).length
  },
  {
    key: 'next_step_or_booking',
    title: 'Есть переход к записи',
    description: 'Предложено время, запланирован перезвон/сообщение или запись уже подтверждена',
    filter: { funnel_stage: 'dental_next_step_or_booking' },
    getCount: (appointmentCalls) => appointmentCalls.filter((item) => NEXT_STEP_OR_BOOKING_STAGES.has(item.funnelStage)).length
  },
  {
    key: 'appointment_booked',
    title: 'Запись подтверждена',
    description: 'Дата, время или договоренность о приеме зафиксированы',
    filter: { funnel_stage: 'appointment_booked' },
    getCount: (appointmentCalls) => appointmentCalls.filter((item) => item.funnelStage === 'appointment_booked').length
  }
];

const COMPLAINT_LABELS = {
  poor_procedure_quality: 'Недовольство результатом процедуры',
  refund_request: 'Запрос возврата',
  negative_review_threat: 'Угроза негативного отзыва',
  authority_complaint_threat: 'Угроза обращения в инстанции',
  service_or_communication: 'Сервис или коммуникация'
};

const normalizeDateRange = (dateRange) => {
  if (!dateRange?.from || !dateRange?.to) return null;
  return {
    date_from: format(dateRange.from, 'yyyy-MM-dd'),
    date_to: format(dateRange.to, 'yyyy-MM-dd')
  };
};

const asArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value) return [];
  return [value].filter(Boolean);
};

const formatPercent = (value, total) => {
  if (!total) return '0%';
  return `${((value * 100) / total).toFixed(1)}%`;
};

const summarize = (items, keyGetter, labelGetter, limit = 8) => {
  const counts = new Map();
  const labels = new Map();
  items.forEach((item) => {
    const key = keyGetter(item);
    if (!key) return;
    counts.set(key, (counts.get(key) ?? 0) + 1);
    labels.set(key, labelGetter(item, key));
  });

  const total = items.length;
  return Array.from(counts.entries())
    .map(([key, count]) => ({
      key,
      label: labels.get(key) ?? key,
      count,
      total,
      valueLabel: `${count} (${formatPercent(count, total)})`
    }))
    .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label, 'ru'))
    .slice(0, limit);
};

const MiniBarChart = ({ title, description, icon: Icon, data, onSelect }) => {
  const maxCount = Math.max(1, ...data.map((item) => item.count));
  return (
    <Card>
      <CardHeader className="space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-5 w-5" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <p className="text-sm text-muted-foreground">Нет данных для отображения.</p>
        ) : (
          <div className="space-y-3 rounded-md bg-muted/30 p-3">
            {data.map((item, index) => (
              <button
                key={item.key}
                type="button"
                onClick={() => onSelect?.(item)}
                className="block w-full rounded-md px-1 py-1 text-left transition-colors hover:bg-background/70 focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <div className="mb-1 flex items-start justify-between gap-3 text-xs">
                  <span className="min-w-0 text-muted-foreground">{item.label}</span>
                  <span className="shrink-0 font-semibold text-foreground">{item.valueLabel}</span>
                </div>
                <div className="h-3 overflow-hidden rounded-full bg-background">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.max(4, (item.count * 100) / maxCount)}%`,
                      backgroundColor: COLORS[index % COLORS.length]
                    }}
                  />
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SalesFunnelCard = ({ items, onOpenCallJournal }) => {
  const appointmentCalls = items.filter((item) => item.callType === 'appointment_or_consultation');
  const counts = Object.fromEntries(
    FUNNEL_STAGES.map((stage) => [stage.key, stage.getCount(appointmentCalls)])
  );
  const totalCalls = counts.appointment_or_consultation ?? 0;
  const conversion = counts.appointment_or_consultation
    ? (counts.appointment_booked * 100) / counts.appointment_or_consultation
    : 0;

  return (
    <Card>
      <CardHeader className="space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2">
          <GitBranch className="h-5 w-5" />
          Воронка продаж
        </CardTitle>
        <CardDescription>
          Конверсия из целевого обращения в подтвержденную запись: {conversion.toFixed(1)}%
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {FUNNEL_STAGES.map((stage, index) => {
          const calls = counts[stage.key] ?? 0;
          const previousStage = FUNNEL_STAGES[index - 1];
          const previousCalls = previousStage ? counts[previousStage.key] ?? 0 : calls;
          const share = totalCalls ? (calls * 100) / totalCalls : 0;
          const stepConversion = index === 0 || !previousCalls ? 100 : (calls * 100) / previousCalls;
          return (
            <button
              key={stage.key}
              type="button"
              onClick={() => onOpenCallJournal?.(stage.filter)}
              className="w-full rounded-md border bg-muted/20 p-3 text-left transition-colors hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium">{stage.title}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{stage.description}</div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-sm font-semibold">{calls.toLocaleString('ru-RU')}</div>
                  <div className="text-xs text-muted-foreground">
                    {share.toFixed(1)}% от входа
                    {index > 0 ? ` · ${stepConversion.toFixed(1)}% от шага` : ''}
                  </div>
                </div>
              </div>
              <div className="mt-3 h-2 rounded-full bg-gray-200 dark:bg-gray-800">
                <div
                  className="h-2 rounded-full bg-blue-600"
                  style={{ width: `${Math.max(4, Math.min(100, share))}%` }}
                />
              </div>
            </button>
          );
        })}
      </CardContent>
    </Card>
  );
};

const DentalDepoDashboardWidgets = ({ dateRange, callType = 'all', onOpenCallJournal }) => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const isFetching = useRef(false);

  const params = useMemo(() => {
    const range = normalizeDateRange(dateRange);
    if (!range) return null;
    return {
      ...range,
      call_type: callType === 'all' ? undefined : callType,
      limit: 500
    };
  }, [callType, dateRange]);

  const load = useCallback(async () => {
    if (!params || isFetching.current) return;
    setError(null);
    isFetching.current = true;
    try {
      const response = await fetchCalls(params);
      setItems(Array.isArray(response?.items) ? response.items : []);
    } catch {
      setError('Не удалось загрузить срезы Дентал Депо.');
      setItems([]);
    } finally {
      isFetching.current = false;
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const complaints = useMemo(
    () => summarize(
      items.flatMap((item) => asArray(item.negativeSubCategories).map((complaint) => ({ complaint }))),
      (item) => item.complaint,
      (item, key) => COMPLAINT_LABELS[key] ?? key,
      8
    ),
    [items]
  );

  const lines = useMemo(
    () => summarize(
      items.filter((item) => item.callType === 'appointment_or_consultation'),
      (item) => item.employeeName,
      (item) => item.employeeName ?? 'Линия не указана',
      8
    ),
    [items]
  );

  return (
    <div className="grid gap-6">
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <SalesFunnelCard items={items} onOpenCallJournal={onOpenCallJournal} />

      <div className="grid gap-6 lg:grid-cols-2">
        <MiniBarChart
          title="Жалобы"
          description="Типы жалоб в клиентских звонках"
          icon={MessageSquareWarning}
          data={complaints}
          onSelect={(item) => onOpenCallJournal?.({ negative_sub_category: item.key })}
        />
        <MiniBarChart
          title="Линии и администраторы"
          description="Звонки записи и консультации по внутренней линии"
          icon={Building2}
          data={lines}
          onSelect={(item) => onOpenCallJournal?.({ department: item.key, call_type: 'appointment_or_consultation' })}
        />
      </div>
    </div>
  );
};

export default DentalDepoDashboardWidgets;
